/**
 * @file PlanTypeDropdown.js
 * @description A dropdown component for selecting a plan type.
 */

import React, { useState, useEffect } from "react";
import { queryPlanTypes } from "../../../services/clientService";
import { Dropdown } from "../../helpers/Dropdown";
import LoadingWrapper from "../../ui/loaders/LoadingWrapper";
import Toast from "../../ui/toasts/Toast";
import Tooltip from "../../ui/tooltips/Tooltip";
import { getText } from '../../../i18n';

/**
 * A dropdown component for selecting plan types.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline.
 * @param {string} props.selectedPlanType - The currently selected plan type ID.
 * @param {Function} props.onPlanTypeChange - The callback function to handle plan type change.
 * @param {string} [props.label="Plan Type"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {string} [props.errorMessage] - The error message to display when validation fails.
 * @param {boolean} [props.hideErrorMessage=false] - Determines whether to hide the error message text.
 * @param {boolean} [props.showToolTip=false] - Determines whether to show a tooltip next to the label.
 * @param {string} [props.tooltipText=""] - The text to display in the tooltip.
 * @param {boolean} [props.disabled=false] - Determines whether the dropdown is disabled.
 * @param {number} [props.tabIndex=0] - The tab order for the dropdown element.
 */

const PlanTypeDropdown = ({
  className,
  inlineLabel = true,
  selectedPlanType,
  onPlanTypeChange,
  label = "Plan Type",
  isRequired = false,
  errorMessage,
  hideErrorMessage = false,
  showToolTip = false,
  tooltipText = "",
  disabled = false,
  tabIndex
}) => {
  const [planTypes, setPlanTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getPlanTypes = async () => {
      setIsLoading(true);
      try {
        const data = await queryPlanTypes();
        setPlanTypes(data);
      } catch (error) {
        console.error("Could not fetch plan types:", error);
        Toast.error(getText('msg_5b5109fce1b3'));
      } finally {
        setIsLoading(false);
      }
    };

    getPlanTypes();
  }, []);

  const getDefaultLabel = () => {
    const matchingPlanType = planTypes.find(
      (planType) => planType.id === selectedPlanType
    );
    return matchingPlanType ? matchingPlanType.name : "Select Plan Type";
  };

  const planTypeOptions = planTypes.map((planType) => ({
    value: planType.id,
    label: planType.name,
  }));

  return (
    <div
      className={`form-group mb-3 ${inlineLabel ? "inline-input" : ""
        } ${className} ${errorMessage ? "is-invalid" : ""}`}
    >
      <label htmlFor="plan-type-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
        {showToolTip && (
          <Tooltip html={tooltipText} placement="right">
            <i
              className="bi bi-info-circle"
              style={{
                paddingLeft: "5px",
                fontSize: "13px",
                color: "var(--iNGAGED-Primary-Color)",
              }}
            ></i>
          </Tooltip>
        )}
      </label>

      <LoadingWrapper isLoading={isLoading}>
        <Dropdown
          id="plan-type-dropdown"
          defaultValue={getDefaultLabel()}
          options={planTypeOptions}
          value={selectedPlanType}
          onChange={onPlanTypeChange}
          disabled={planTypes.length === 0 || isLoading || disabled}
          tabIndex={tabIndex}
        />
      </LoadingWrapper>
      {errorMessage && !hideErrorMessage && (
        <div className="invalid-feedback d-block">{errorMessage}</div>
      )}
    </div>
  );
};

export default PlanTypeDropdown;
